import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import Dialog from 'ui/Dialog';
import { useFileUpload } from 'ui/FileUpload';
import Icon from 'ui/Icon';
import { toaster } from 'ui/Toaster';
import { sankeyToForm } from 'utils/form';

import { pathToSankey } from '../../../utils/routes';
import { useTeamContext } from '../../../utils/team';
import { updateNodesFromFlows } from '../../SankeyChart/utils';
import { useNewSankeyForm } from '../../SankeyFormProvider';
import CreateFromDraftPage from './CreateFromDraftPage';
import ImportFilesUploadPage from './ImportFilesUploadPage';
import { fileUploadProps } from './constants';

enum Page {
  ImportFiles = 'import-files',
  DraftPreview = 'draft-preview',
}

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

// TODO: Rename to SmartImportDialog
const SmartImportModal: React.FC<Props> = ({ open, setOpen }) => {
  const { teamSettings } = useTeamContext();
  const draftForm = useNewSankeyForm();
  const navigate = useNavigate();

  const [page, setPage] = useState(Page.ImportFiles);
  const fileUpload = useFileUpload(fileUploadProps);

  // Reset file upload state when dialog is opened
  useEffect(() => {
    if (open) {
      fileUpload.clearFiles();
      setPage(Page.ImportFiles);
    }
  }, [open]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
        if (!e.open) {
          analytics.track('Smart Import Dialog - Close');
        }
      }}
      closeOnInteractOutside={false} // Important uploaded data can be lost accidentally
      size="lg"
    >
      <Dialog.Content>
        <Dialog.Header>
          <Icon fontSize="md" icon="upload" mr={2} /> Smart import
        </Dialog.Header>
        <Dialog.Body>
          {page === Page.ImportFiles && (
            <ImportFilesUploadPage
              fileUpload={fileUpload}
              onGeneratedDraft={(sankey) => {
                draftForm.reset(sankeyToForm(sankey));
                draftForm.setValue('nodes', {}); // Trick to set fresh colors on all nodes below
                updateNodesFromFlows(draftForm, teamSettings);
                setPage(Page.DraftPreview);
              }}
            />
          )}
          {page === Page.DraftPreview && (
            <CreateFromDraftPage
              draftForm={draftForm}
              onGoBack={() => setPage(Page.ImportFiles)}
              onCreateDiagram={(sankey) => {
                toaster.success({ title: 'Sankey diagram created' });
                navigate(pathToSankey(sankey.id));
                setOpen(false);
              }}
            />
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default SmartImportModal;
